import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";

export default function ChangePassword() {
  const { user, changePassword } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-stone-500 text-sm tracking-wide">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Already changed — no need to be here
  if (!user.must_change_password) return <Navigate to="/scenarios" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await changePassword(current, next);
      toast.success("Password changed successfully");
      navigate("/scenarios");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar minimal />
      <div className="max-w-md mx-auto px-6 pt-16 pb-24">
        <div className="text-xs tracking-[0.18em] uppercase text-stone-500 mb-3">Security</div>
        <h1 className="font-display text-3xl md:text-4xl text-stone-900 tracking-tight">Change your password.</h1>
        <p className="mt-3 text-stone-600">
          You&apos;re using a temporary password. Please set a new password to continue.
        </p>

        <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="change-password-form">
          <div>
            <Label htmlFor="current" className="text-sm font-medium text-stone-700 mb-2 block">Current (temporary) password</Label>
            <Input
              id="current"
              data-testid="current-password-input"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="rounded-xl h-12 bg-white border-stone-200 focus-visible:ring-[#b81f24]/20 focus-visible:border-[#b81f24]"
            />
          </div>
          <div>
            <Label htmlFor="next" className="text-sm font-medium text-stone-700 mb-2 block">New password</Label>
            <Input
              id="next"
              data-testid="new-password-input"
              type="password"
              autoComplete="new-password"
              minLength={6}
              required
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="rounded-xl h-12 bg-white border-stone-200 focus-visible:ring-[#b81f24]/20 focus-visible:border-[#b81f24]"
            />
            <p className="text-xs text-stone-500 mt-1.5">At least 6 characters, different from the temporary one.</p>
          </div>
          <div>
            <Label htmlFor="confirm" className="text-sm font-medium text-stone-700 mb-2 block">Confirm new password</Label>
            <Input
              id="confirm"
              data-testid="confirm-password-input"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="rounded-xl h-12 bg-white border-stone-200 focus-visible:ring-[#b81f24]/20 focus-visible:border-[#b81f24]"
            />
          </div>
          <Button
            data-testid="change-password-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-md bg-[#b81f24] hover:bg-[#9a1a1f] text-white text-base"
          >
            {loading ? "Saving…" : "Change password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
